import type { ModuleDescriptor } from "src/app/moduleDescriptor";
import type { NavItem, NavItems } from "src/components/navigation/navItems";
import { useSession, type SessionState } from "src/features/session";

/**
 * Whether the user may open a module: it needs no entitlement, or the user
 * holds at least one of its `requiredEntitlements` (exact names). The route
 * guard and the drawer both decide with this, so a hidden nav item is always
 * also a blocked route.
 */
export function canAccess(
  module: Pick<ModuleDescriptor, "requiredEntitlements">,
  entitlements: readonly string[]
): boolean {
  return (
    !module.requiredEntitlements ||
    module.requiredEntitlements.some((required) => entitlements.includes(required))
  );
}

/**
 * The drawer's items: modules with a nav entry that the user may open, by
 * section, in registry order.
 */
export function buildNavItems(
  modules: readonly ModuleDescriptor[],
  entitlements: readonly string[]
): NavItems {
  const items: NavItems = { primary: [], secondary: [] };
  for (const module of modules) {
    if (!module.nav || !canAccess(module, entitlements)) continue;
    const item: NavItem = {
      id: module.id,
      label: module.nav.label,
      icon: module.nav.icon,
      path: module.path,
    };
    items[module.nav.section].push(item);
  }
  return items;
}

/**
 * The user's entitlements, or `null` when the session failed to load and they
 * are unknown. The drawer and the route guard both read them through this.
 */
export function knownEntitlements(session: SessionState): readonly string[] | null {
  return session.status === "ready" ? session.entitlements : null;
}

/**
 * The caller's entitlements for the drawer: none when unknown, so gated items
 * are hidden (fail closed). The API enforces the same entitlement on its
 * endpoints; this only decides what the shell shows.
 */
export function useGrantedEntitlements(): readonly string[] {
  return knownEntitlements(useSession()) ?? [];
}
